import { getFacilityLevelsOfCareQueryOptions } from '@/entities/facility/api/get-facility-levels-of-care.query';
import type { FacilityLevelsOfCareDto } from '@/shared/api/modules/facilities';
import { chartColor } from '@/shared/lib/chart-theme';
import { cn } from '@/shared/lib/cn';
import {
  getIncompleteMonthTitles,
  getIncompletePeriods,
} from '@/shared/lib/incomplete-periods';
import { getWholeNumberAxis } from '@/shared/lib/whole-number-axis';
import { Loader, TooltipElement, Typography } from '@/shared/ui';
import { MultiLineChart } from '@/shared/ui/charts/multiline-chart';
import { useQuery } from '@tanstack/react-query';
import type { ChartData, ChartOptions, PointStyle } from 'chart.js';
import type { Dayjs } from 'dayjs';
import { type FC } from 'react';

// A function, not a constant: `chartColor` reads the live CSS variables, so
// evaluating this at module scope would freeze it to whichever theme happened to
// be active at import time.
const levelsColors = () => [
  { hovered: '#1750ac', default: '#98b2dd', badge: 'bg-primary-6' },
  {
    hovered: chartColor('--color-volcano-6'),
    default: '#ff9c6e',
    badge: 'bg-volcano-6',
  },
  {
    hovered: chartColor('--color-polar-green-6'),
    default: chartColor('--color-polar-green-4'),
    badge: 'bg-polar-green-6',
  },
  { hovered: '#722ed1', default: '#d3adf7', badge: 'bg-golden-purple-6' },
  {
    hovered: chartColor('--color-calendula-gold-9'),
    default: '#ffc069',
    badge: 'bg-calendula-gold-6',
  },
  {
    hovered: chartColor('--color-dust-red-7'),
    default: chartColor('--color-dust-red-4'),
    badge: 'bg-dust-red-6',
  },
];

type FacilityLevelsOfCareChartProps = {
  /** Omit for the All Facilities view. */
  facilityId?: string;
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
  hoveredLevel: number | null;
  onLevelLabelHover: (data: FacilityLevelsOfCareDto) => void;
  onHoverEnd: () => void;
};

export const FacilityLevelsOfCareChart: FC<FacilityLevelsOfCareChartProps> = ({
  facilityId,
  dateRange,
  hoveredLevel,
  onLevelLabelHover,
  onHoverEnd,
}) => {
  const { data: levelsOfCareResponse, isPending } = useQuery(
    getFacilityLevelsOfCareQueryOptions({
      facilityId,
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      perPage: 10,
      page: 1,
    })
  );

  const chartData = levelsOfCareResponse?.chart;
  const values = chartData?.datasets.flatMap((d) => d.data) ?? [];

  const from = dateRange.from.format('YYYY-MM-DD');
  const to = dateRange.to.format('YYYY-MM-DD');
  const incompletePeriods = chartData
    ? getIncompletePeriods({
        from,
        to,
        granularity: chartData.granularity,
        bucketCount: chartData.labels.length,
        firstBucketStart: from,
        lastBucketEnd: to,
      })
    : undefined;

  // Same text as this chart's week labels ("Sep 8, 2026 – Sep 14, 2026").
  const incompleteMonthTitles = getIncompleteMonthTitles(incompletePeriods, {
    padDay: false,
    dash: '–',
  });

  // Residents per level: whole numbers only, starting at 0.
  const { max, stepSize } = getWholeNumberAxis(Math.max(0, ...values), {
    intervals: 4,
    headroom: 0.1,
  });

  const options: ChartOptions<'line'> = {
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          title: (items) =>
            incompleteMonthTitles?.[items[0]?.dataIndex] ?? items[0]?.label,
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max,
        ticks: {
          stepSize,
          precision: 0,
        },
      },
    },
  };

  const formattedChartData: ChartData<'line'> = {
    labels: chartData?.labels ?? [],
    datasets:
      chartData?.datasets?.map((dataset, index) => ({
        ...dataset,
        label: `Level ${dataset.levelOfCare}`,
        pointRadius: 3,
        pointBackgroundColor: 'white',
        backgroundColor: 'transparent',
        borderColor:
          hoveredLevel === dataset.levelOfCare
            ? levelsColors()[index]?.hovered
            : levelsColors()[index]?.default,
        pointStyle: (hoveredLevel === dataset.levelOfCare
          ? 'circle'
          : false) as PointStyle,
      })) ?? [],
  };

  if (isPending) {
    return (
      <div className="border-neutral-4 flex h-80 items-center justify-center border bg-white p-3">
        <Loader />
      </div>
    );
  }

  return (
    <div className="border-neutral-4 flex flex-col border bg-white p-3">
      <div className="mb-3 flex items-center gap-x-2">
        <Typography
          as="h5"
          variant="h5"
          weight="medium"
          className="text-neutral-12"
        >
          Residents Month over month
        </Typography>
        <TooltipElement content="Each line represents the number of residents assigned to a specific Level of Care for each period" />
      </div>

      <div className="mb-6 grid grid-cols-[1fr_auto] items-start gap-6">
        <div className="h-83">
          <MultiLineChart
            optionsProps={options}
            data={formattedChartData}
            legendProps={{ display: false }}
            incompletePeriods={incompletePeriods}
          />
        </div>
        <div className="border-neutral-5 w-46 border" onMouseLeave={onHoverEnd}>
          {chartData?.datasets?.map((item, index) => {
            return (
              <div
                className="border-neutral-5 hover:bg-neutral-2 flex cursor-pointer items-center gap-2 border-b px-2 py-4"
                key={index}
                onMouseEnter={() => {
                  onLevelLabelHover({
                    levelOfCare: item.levelOfCare,
                  } as FacilityLevelsOfCareDto);
                }}
              >
                <div
                  className={cn(
                    'h-1.5 w-1.5',
                    levelsColors()[item.levelOfCare - 1]?.badge
                  )}
                />
                <Typography>Level of Care {item.levelOfCare}</Typography>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
